import { useFormikContext } from 'formik';
import { filter, find, has, isNull, uniqBy } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FormControls, FormikSelect } from 'components';
import { AppConfigArea } from 'enums';
import { usePrevious } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { AppConfig, AppConfigSchema, Bank } from 'types';
import { appConfigUtils } from 'utils';

import { ConfigValueField } from '../../ConfigValueField';

type Props = {
  data?: Partial<AppConfig>;
  banks: Bank[];
  schemas: AppConfigSchema[];
  onKeyChange: (key: string) => void;
};

enum EntityName {
  BankId = 'bankId',
  P2PProviderType = 'p2pProviderType',
  PayoutRequisitesAutomationType = 'payoutRequisitesAutomationType',
}

const ENTITY_LABELS: Record<EntityName, string> = {
  [EntityName.BankId]: 'fields.bank',
  [EntityName.P2PProviderType]: 'fields.provider',
  [EntityName.PayoutRequisitesAutomationType]:
    'fields.payout_requisites_automation_type',
};

export const ConfigForm: React.FC<Props> = ({
  data,
  banks,
  schemas,
  onKeyChange,
}) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'features.configs',
  });

  const formik = useFormikContext<any>();

  const isAnyBank = useMemo(
    () =>
      data?.area === AppConfigArea.PayinRequisites &&
      find(
        schemas,
        (schema) =>
          schema.key === formik.values.key &&
          has(schema, 'bankId') &&
          isNull(schema.bankId),
      ),
    [data?.area, formik.values.key, schemas],
  );

  const entityName = useMemo(() => {
    if (
      data?.area === AppConfigArea.PayinP2PProvider ||
      data?.area === AppConfigArea.PayoutP2PProvider
    ) {
      return EntityName.P2PProviderType;
    } else if (data?.area === AppConfigArea.PayoutRequisitesAutomation) {
      return EntityName.PayoutRequisitesAutomationType;
    } else if (
      data?.area === AppConfigArea.PayinRequisites &&
      find(
        schemas,
        (schema) => schema.key === formik.values.key && has(schema, 'bankId'),
      )
    ) {
      return EntityName.BankId;
    }
    return null;
  }, [data?.area, formik.values.key, schemas]);

  const prevEntityName = usePrevious(entityName);

  const entityLabel = useMemo(() => {
    const key = entityName ? ENTITY_LABELS[entityName] : null;
    return t(key as any);
  }, [t, entityName]);

  const entityOptions = useMemo(() => {
    if (!entityName) {
      return [];
    }
    const mapFn = appConfigUtils.getEntityTypeLabel(data?.area, banks);
    if (entityName === EntityName.BankId) {
      return banks.map((bank) => ({
        label: mapFn(bank.id),
        value: bank.id,
      }));
    }
    return filter(schemas, (schema) => schema.key === formik.values.key).map(
      (schema) => ({
        label: mapFn(schema[entityName]),
        value: schema[entityName]!,
      }),
    );
  }, [entityName, data?.area, banks, schemas, formik.values.key]);

  const keyOptions = useMemo(
    () =>
      uniqBy(schemas, 'key').map((schema) => ({
        label: appConfigUtils.getKeyLabel(schema.key),
        value: schema.key,
      })),
    [schemas],
  );

  const keySchema = useMemo(() => {
    if (entityName) {
      if (isAnyBank) {
        const schema = find(
          schemas,
          (schema) => schema.key === formik.values.key,
        );
        if (schema) {
          return {
            ...schema,
            bankId: formik.values.bankId,
          };
        }
        return null;
      }
      return find(
        schemas,
        (schema) =>
          schema[entityName] === formik.values[entityName] &&
          schema.key === formik.values.key,
      );
    }
    return find(schemas, (schema) => schema.key === formik.values.key);
  }, [isAnyBank, entityName, schemas, formik.values]);

  const handleKeyChange = useCallback(
    (key: string) => {
      if (prevEntityName) {
        formik.setFieldValue(prevEntityName, '');
        formik.setFieldTouched(prevEntityName, false, true);
      }
      onKeyChange(key);
    },
    [prevEntityName, onKeyChange, formik],
  );

  useEffect(() => {
    if (prevEntityName && entityName !== prevEntityName) {
      delete formik.values[prevEntityName];
    }
  }, [entityName, formik, formik.values, prevEntityName]);

  return (
    <FormControls>
      <FormikSelect
        label={t('fields.key')}
        name="key"
        options={keyOptions}
        disabled={!!data?.id || keyOptions.length === 0}
        required
        noneOption
        onChange={handleKeyChange}
      />
      {entityName && formik.values.key && (
        <FormikSelect
          label={entityLabel}
          name={entityName}
          options={entityOptions}
          disabled={!!data?.id}
          required
          noneOption
          disablePortal={false}
        />
      )}
      {keySchema && (!entityName || formik.values[entityName]) && data && (
        <ConfigValueField schema={keySchema} data={data} />
      )}
    </FormControls>
  );
};
